import React, { useState, useEffect } from 'react';
import euLogo from '../assets/eu-logo.png';
import unLogo from '../assets/un-logo.png';
import { useTranslation } from '../hooks/useTranslation';
import translations from '../translations';

// This component uses the translation hook for normal rendering in the app
const SanctionsReport = React.forwardRef(({ data }, ref) => {
  const t = useTranslation();
  return <SanctionsReportContent data={data} t={t} ref={ref} />;
});

// This is a hook-free component that can be used for PDF generation
export const SanctionsReportStandalone = React.forwardRef(({ data, language = 'en' }, ref) => {
  // Create a standalone translation function that doesn't rely on hooks
  const t = (key, replacements = {}) => {
    const translationObj = translations[language] || translations.en;
    
    // Helper function to get nested property using dot notation
    const getNestedValue = (obj, path) => {
      const keys = path.split('.');
      return keys.reduce((o, key) => (o && o[key] !== undefined ? o[key] : undefined), obj);
    };
    
    // Get text from translations
    let text = getNestedValue(translationObj, key);
    if (text === undefined) {
      // Fallback to English if the key doesn't exist in the selected language
      text = getNestedValue(translations.en, key) || key;
    }
    
    // Replace any placeholders like {{count}}
    return Object.entries(replacements).reduce(
      (str, [key, value]) => str.replace(new RegExp(`{{${key}}}`, 'g'), value),
      text
    );
  };

  return <SanctionsReportContent data={data} t={t} ref={ref} />;
});

// Shared content component that receives the translation function as a prop
const SanctionsReportContent = React.forwardRef(({ data, t }, ref) => {
  // Store the entity type in state to prevent it from changing when the user toggles the search type
  const [finalEntityType, setFinalEntityType] = useState(data.entityType || 'person');
  
  // Set the entity type once when the component mounts or when data changes
  useEffect(() => {
    setFinalEntityType(data.entityType || 'person');
  }, [data.entityType]);
  
  // Determine if we're dealing with an individual or entity
  // Improved entity type detection with case-insensitive check and multiple possible values
  const entityTypeValue = (finalEntityType || '').toLowerCase();
  const isEntity = entityTypeValue === 'enterprise' || 
                  entityTypeValue === 'company' || 
                  entityTypeValue === 'organization' || 
                  entityTypeValue === 'entity';
  const subjectType = isEntity ? 'entity' : 'individual';
  
  return (
    <div 
      ref={ref} 
      className="sanctions-report"
      style={{ 
        fontFamily: 'Segoe UI, Arial, sans-serif',
        width: '100%',
        height: '100%',
        color: '#333',
        backgroundColor: 'white',
        position: 'relative',
        padding: '0',
        margin: '0',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <div style={{ 
        backgroundColor: '#1e293b', 
        color: 'white',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ 
            width: '32px', 
            height: '32px', 
            backgroundColor: '#fff', 
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#1e293b' }}>
              <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M8 12L10.5 14.5L16 9" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <h1 style={{ 
            fontSize: '18px', 
            fontWeight: 'bold', 
            margin: 0, 
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {t('common.appName', {defaultValue: 'Protego'})} {t('report.title', {defaultValue: 'Screening Report'})}
          </h1>
        </div>
        <div style={{ fontSize: '12px' }}>
          <span style={{ opacity: 0.75 }}>{t('results.verificationId')}:</span> <span style={{ fontFamily: 'monospace', fontWeight: 'medium' }}>{data.verificationId}</span>
        </div>
      </div>
      
      {/* Main Content */}
      <div style={{ padding: '0 24px 24px' }}>
        {/* Status Banner */}
        <div style={{ 
          backgroundColor: 'rgba(16, 185, 129, 0.1)', 
          border: '1px solid #10b981',
          borderRadius: '4px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            backgroundColor: '#10b981', 
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '16px'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: 'white' }}>
              <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h2 style={{ 
              color: '#065f46', 
              fontWeight: 'bold', 
              fontSize: '20px',
              margin: '0 0 4px 0'
            }}>
              {t('results.noSanctionsFound')}
            </h2>
            <p style={{ 
              color: '#065f46', 
              margin: 0,
              fontSize: '14px'
            }}>
              {data.name} {t(`results.cleared${subjectType === 'entity' ? 'Entity' : ''}`, {defaultValue: `does not appear on any sanctions lists. This ${subjectType} has been cleared against all monitored sanctions databases.`})}
            </p>
          </div>
        </div>
        
        {/* Subject Information */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ 
            fontSize: '12px', 
            textTransform: 'uppercase', 
            letterSpacing: '0.05em',
            color: '#64748b',
            margin: '0 0 8px 0',
            fontWeight: '500'
          }}>
            {t('report.subjectInformation', {defaultValue: 'Subject Information'})}
          </h3>
          <div style={{ 
            backgroundColor: '#f8fafc', 
            border: '1px solid #e2e8f0',
            padding: '16px',
            borderRadius: '4px'
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'row',
              justifyContent: 'space-between',
              flexWrap: 'wrap'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 4px 0' }}>{t('report.name', {defaultValue: 'Name'})}</p>
                <p style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  color: '#1e293b',
                  margin: '0'
                }}>
                  {data.name}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 4px 0' }}>{t('report.generated', {defaultValue: 'Report Generated'})}</p>
                <p style={{ 
                  fontSize: '16px', 
                  fontWeight: '500', 
                  color: '#1e293b',
                  margin: '0'
                }}>
                  {data.timestamp}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Screening Details */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ 
            fontSize: '12px', 
            textTransform: 'uppercase', 
            letterSpacing: '0.05em',
            color: '#64748b',
            margin: '0 0 8px 0',
            fontWeight: '500'
          }}>
            {t('report.screeningDetails', {defaultValue: 'Screening Details'})}
          </h3>
          <div style={{ 
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <table style={{ 
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <tbody>
                <tr>
                  <td style={{ 
                    padding: '12px 16px', 
                    backgroundColor: '#f8fafc',
                    width: '33%',
                    borderBottom: '1px solid #e2e8f0',
                    color: '#64748b',
                    fontWeight: '500'
                  }}>
                    {t('results.recordsScanned')}
                  </td>
                  <td style={{ 
                    padding: '12px 16px',
                    borderBottom: '1px solid #e2e8f0',
                    color: '#1e293b'
                  }}>
                    {data.recordsScanned?.toLocaleString() || '0'}
                  </td>
                </tr>
                <tr>
                  <td style={{ 
                    padding: '12px 16px', 
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    color: '#64748b',
                    fontWeight: '500'
                  }}>
                    {t('results.searchDate')}
                  </td>
                  <td style={{ 
                    padding: '12px 16px',
                    borderBottom: '1px solid #e2e8f0',
                    color: '#1e293b'
                  }}>
                    {data.timestamp}
                  </td>
                </tr>
                <tr>
                  <td style={{ 
                    padding: '12px 16px', 
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    color: '#64748b',
                    fontWeight: '500'
                  }}>
                    {t('results.validUntil')}
                  </td>
                  <td style={{ 
                    padding: '12px 16px',
                    borderBottom: '1px solid #e2e8f0',
                    color: '#1e293b'
                  }}>
                    {data.expiryDate}
                  </td>
                </tr>
                <tr>
                  <td style={{ 
                    padding: '12px 16px', 
                    backgroundColor: '#f8fafc',
                    color: '#64748b',
                    fontWeight: '500',
                    verticalAlign: 'top'
                  }}>
                    {t('results.sourcesChecked')}
                  </td>
                  <td style={{ 
                    padding: '12px 16px',
                    color: '#1e293b'
                  }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {data.sources?.map((source, index) => (
                        <div key={index} style={{ 
                          backgroundColor: '#f1f5f9',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#1e293b',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ marginRight: '4px', color: '#10b981' }}>
                            <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {source}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Authorities Section */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ 
            fontSize: '12px', 
            textTransform: 'uppercase', 
            letterSpacing: '0.05em',
            color: '#64748b',
            margin: '0 0 8px 0',
            fontWeight: '500'
          }}>
            {t('report.authoritiesChecked', {defaultValue: 'Authorities Checked'})}
          </h3>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px'
          }}>
            {/* EU Financial & Travel Sanctions */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '16px', 
              border: '1px solid #e2e8f0',
              borderRadius: '4px'
            }}>
              <div style={{ 
                width: '80px', 
                height: '48px', 
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}>
                <img src={euLogo} alt="EU Logo" style={{ maxWidth: '72px', maxHeight: '44px', objectFit: 'contain' }} />
              </div>
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontWeight: '500', color: '#1e293b' }}>{t('authorities.euCommission')}</h4>
                <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{t('authorities.euCommissionDescription')}</p>
              </div>
            </div>
            
            {/* UN */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '16px', 
              border: '1px solid #e2e8f0',
              borderRadius: '4px'
            }}>
              <div style={{ 
                width: '48px', 
                height: '48px', 
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}>
                <img src={unLogo} alt="UN Logo" style={{ maxWidth: '44px', maxHeight: '44px', objectFit: 'contain' }} />
              </div>
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontWeight: '500', color: '#1e293b' }}>{t('authorities.unitedNations')}</h4>
                <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{t('authorities.unitedNationsDescription')}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Run By Information */}
        {data.runBy && (
          <div style={{ marginTop: '16px', fontSize: '13px', color: '#64748b' }}>
            <p style={{ margin: '0' }}>
              {t('report.runBy', {defaultValue: 'Run by'})}: {data.runBy} {data.runByEmail ? `(${data.runByEmail})` : ''}
            </p>
          </div>
        )}
        
        {/* Verification */}
        <div style={{ marginTop: '24px' }}>
          <h3 style={{ 
            fontSize: '12px', 
            textTransform: 'uppercase', 
            letterSpacing: '0.05em',
            color: '#64748b',
            margin: '0 0 8px 0',
            fontWeight: '500'
          }}>
            {t('report.verification', {defaultValue: 'Verification'})}
          </h3>
          <div style={{ 
            backgroundColor: '#f8fafc', 
            border: '1px solid #e2e8f0',
            padding: '16px',
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            {/* Issued by */}
            <div style={{ marginBottom: '16px' }}>
              <span style={{ color: '#64748b', marginRight: '8px', fontWeight: '500' }}>{t('report.issuedBy', {defaultValue: 'Issued by'})}:</span>
              <span style={{ color: '#1e293b', fontWeight: '500' }}>{t('report.issuedByValue', {defaultValue: 'Protego'})}</span>
            </div>
            
            {/* Verify at */}
            <div style={{ marginBottom: '16px' }}>
              <span style={{ color: '#64748b', marginRight: '8px', fontWeight: '500' }}>{t('report.verifyAt', {defaultValue: 'Verify at'})}:</span>
              <span style={{ 
                display: 'inline-block',
                backgroundColor: '#f1f5f9',
                border: '1px solid #e2e8f0',
                padding: '4px 8px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '12px',
                color: '#475569'
              }}>
                app.sanctionscreener.eu/verification/{data.verificationId}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div style={{ 
        backgroundColor: '#f8fafc',
        borderTop: '1px solid #e2e8f0',
        padding: '12px 24px',
        fontSize: '12px',
        color: '#64748b',
        display: 'flex',
        justifyContent: 'space-between',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0
      }}>
        <div>{t('report.generatedOn')} {data.timestamp}</div>
        <div>{t('report.verificationId')} {data.verificationId}</div>
      </div>
    </div>
  );
});

export default SanctionsReport;